import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useBackTarget } from '../../hooks/useBackTarget';

const font = "'Inria Serif', serif";
const SUPPORT_EMAIL = 'contact@allurexp.com';
const LAST_UPDATED = 'February 3, 2026';

const COMPANY = {
  name: 'ALLURE EXPERIENCE I.K.E.',
  nameLatin: 'ALLURE EXPERIENCE P.C.',
  distinctive: 'ALLUREXP',
  legalForm: 'IKE (Private Company / Ιδιωτική Κεφαλαιουχική Εταιρεία)',
  vat: 'EL803335274',
  gemi: '194817609000',
  taxOffice: 'ΚΕΦΟΔΕ ΑΤΤΙΚΗΣ',
  chamber: 'Επαγγελματικό Επιμελητήριο Πειραιώς',
  addressLatin: 'Emmanouil Dimela 74, 16674 Athens, Greece',
  address: 'Εμμανουήλ Δημελά 74, 16674 Αθήνα, Ελλάδα',
  phone: '+30 694 396 3291',
  administrator: 'Horatiu Marian Onofrei',
};

const sectionStyle = { marginTop: 28 };
const h2Style = {
  fontFamily: font,
  fontSize: 22,
  fontWeight: 700,
  color: '#111',
  margin: '0 0 10px',
};
const h3Style = {
  fontFamily: font,
  fontSize: 17,
  fontWeight: 700,
  color: '#111',
  margin: '16px 0 6px',
};
const pStyle = {
  fontFamily: font,
  fontSize: 15,
  lineHeight: 1.6,
  color: '#333',
  margin: '0 0 10px',
};
const listStyle = {
  fontFamily: font,
  fontSize: 15,
  lineHeight: 1.7,
  color: '#333',
  paddingLeft: 22,
  margin: '0 0 10px',
};

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();
  const goBack = useBackTarget();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div
      data-testid="privacy-policy-page"
      style={{
        minHeight: '100vh',
        backgroundColor: '#FFFFFF',
        padding: '20px 20px 80px',
        maxWidth: 820,
        margin: '0 auto',
      }}
    >
      <button
        type="button"
        onClick={goBack}
        data-testid="privacy-back-button"
        aria-label="Back"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          border: 'none',
          background: 'transparent',
          fontFamily: font,
          fontSize: 15,
          color: '#703FF6',
          cursor: 'pointer',
          padding: 0,
          marginBottom: 14,
        }}
      >
        <ArrowLeft size={18} /> Back
      </button>

      <h1
        style={{
          fontFamily: font,
          fontSize: 30,
          fontWeight: 700,
          color: '#111',
          margin: 0,
        }}
      >
        Allure — Privacy Policy
      </h1>
      <p style={{ ...pStyle, color: '#666', marginTop: 8 }}>
        Last updated: {LAST_UPDATED}
      </p>

      <section style={sectionStyle}>
        <p style={pStyle}>
          <strong>{COMPANY.nameLatin}</strong> (Greek legal name: <em>{COMPANY.name}</em>, distinctive title "{COMPANY.distinctive}"), a private company incorporated in Greece, operator of the Allure marketplace and of <a href="https://allurexp.com" style={{ color: '#703FF6' }}>allurexp.com</a> ("we", "us", "our"), provides a mobile and web service that connects guests with partner venues and hosts.
          This Privacy Policy explains what personal data we collect, why we collect it, how we use it,
          who we share it with, and what rights you have over it.
        </p>
        <p style={pStyle}>
          This policy is written to satisfy the EU General Data Protection Regulation (GDPR 2016/679),
          the UK Data Protection Act 2018, and the California Consumer Privacy Act (CCPA). If you have
          questions, contact our Data Protection Officer at{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: '#703FF6' }}>
            {SUPPORT_EMAIL}
          </a>
          .
        </p>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>1. Who is the data controller?</h2>
        <p style={pStyle}>
          The data controller for personal information collected through the Allure app, website
          (<a href="https://allurexp.com" style={{ color: '#703FF6' }}>allurexp.com</a>) and related
          services is:
        </p>
        <ul style={listStyle}>
          <li><strong>Legal name:</strong> {COMPANY.name} (Latin: {COMPANY.nameLatin})</li>
          <li><strong>Distinctive title:</strong> {COMPANY.distinctive}</li>
          <li><strong>Legal form:</strong> {COMPANY.legalForm}</li>
          <li><strong>Registered seat:</strong> {COMPANY.addressLatin}</li>
          <li><strong>Γ.Ε.ΜΗ. (GEMI):</strong> {COMPANY.gemi}</li>
          <li><strong>VAT / ΑΦΜ:</strong> {COMPANY.vat}</li>
          <li><strong>Tax office (ΔΟΥ):</strong> {COMPANY.taxOffice}</li>
          <li><strong>Chamber:</strong> {COMPANY.chamber}</li>
          <li><strong>Legally represented by:</strong> {COMPANY.administrator}, administrator</li>
          <li>
            <strong>Data Protection contact:</strong>{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: '#703FF6' }}>{SUPPORT_EMAIL}</a>
          </li>
        </ul>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>2. Information we collect</h2>

        <h3 style={h3Style}>2.1 Information you give us</h3>
        <ul style={listStyle}>
          <li>
            <strong>Account data:</strong> full name, email address, phone number (optional),
            password (stored hashed, never in plaintext), date of birth (for age-rating where
            required).
          </li>
          <li>
            <strong>Profile data:</strong> profile photo, preferred role (guest / host / venue owner),
            language.
          </li>
          <li>
            <strong>Venue / host onboarding data:</strong> business name, business address, contact
            details, social media handles, venue photos, Airbnb listing URLs, bank-transfer details
            (IBAN) for payouts.
          </li>
          <li>
            <strong>Content you create:</strong> messages sent inside the in-app chat, reviews,
            tip amounts, staff invitations.
          </li>
        </ul>

        <h3 style={h3Style}>2.2 Information collected automatically</h3>
        <ul style={listStyle}>
          <li>
            <strong>Transaction data:</strong> payment amounts, discount applied, staff tip, venue
            identifier, timestamp, payment status. Card details are handled by our payment
            processor and <strong>never stored on Allure servers</strong>.
          </li>
          <li>
            <strong>QR / pass data:</strong> when you scan an Allure QR pass, we record a
            transaction reference, venue ID, and the time of the scan.
          </li>
          <li>
            <strong>Approximate location:</strong> when you grant location permission, we use it
            only to show nearby partner venues on the map. We do not continuously track your
            location in the background.
          </li>
          <li>
            <strong>Device & log data:</strong> device type, operating system, app version, IP
            address, crash logs, page views, session duration.
          </li>
          <li>
            <strong>Cookies and local storage:</strong> session tokens, preferences, and PWA
            installation identifiers. See Section 9.
          </li>
        </ul>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>3. Why we use your data (legal bases)</h2>
        <ul style={listStyle}>
          <li>
            <strong>To provide the service</strong> — account creation, authentication, scanning
            passes, processing payments, delivering messages, calculating host earnings and venue
            payouts. Legal basis: performance of a contract (GDPR Art. 6(1)(b)).
          </li>
          <li>
            <strong>Operational messages from Allure Support</strong> — messages sent to you from
            the Allure Support team (support conversations, account, safety and legal
            notifications) are considered operational and are <strong>always delivered</strong>,
            regardless of your in-app notification preferences or privacy toggles. The only way to
            stop them is to disable Allure notifications entirely at the operating system level
            (or to never grant them in the first place). Legal basis: performance of a contract
            and legitimate interest in providing a safe, functioning service (Art. 6(1)(b) and
            Art. 6(1)(f)).
          </li>
          <li>
            <strong>To comply with the law</strong> — tax, accounting, anti-money-laundering,
            fraud prevention. Legal basis: legal obligation (Art. 6(1)(c)).
          </li>
          <li>
            <strong>To improve the service</strong> — aggregated, non-personally-identifiable
            analytics. Legal basis: legitimate interest (Art. 6(1)(f)).
          </li>
          <li>
            <strong>To send you transactional emails</strong> — receipts, account verification,
            early-access approval, password reset. Legal basis: performance of a contract.
          </li>
          <li>
            <strong>To send you marketing emails</strong> — only with your prior opt-in consent
            (Art. 6(1)(a)). You can withdraw consent at any time from Settings → Privacy.
          </li>
        </ul>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>4. Who we share your data with</h2>
        <p style={pStyle}>
          We share your personal data only with the following categories of recipients, and only
          to the extent necessary:
        </p>
        <ul style={listStyle}>
          <li>
            <strong>Payment processor — Viva Wallet (Viva Payments S.A., Greece)</strong>: card
            tokenisation, payment authorisation, settlement. Viva Wallet is a separate data
            controller under PSD2. See{' '}
            <a
              href="https://www.vivawallet.com/gr_en/privacy-policy"
              target="_blank"
              rel="noreferrer"
              style={{ color: '#703FF6' }}
            >
              Viva Wallet privacy policy
            </a>
            .
          </li>
          <li>
            <strong>Transactional email provider — Resend (Resend, Inc., USA)</strong>: delivery of
            account and transaction emails. See{' '}
            <a
              href="https://resend.com/legal/privacy-policy"
              target="_blank"
              rel="noreferrer"
              style={{ color: '#703FF6' }}
            >
              Resend privacy policy
            </a>
            .
          </li>
          <li>
            <strong>Voice / video infrastructure — Twilio (Twilio Inc., USA)</strong>: TURN
            servers for in-app calls. See{' '}
            <a
              href="https://www.twilio.com/legal/privacy"
              target="_blank"
              rel="noreferrer"
              style={{ color: '#703FF6' }}
            >
              Twilio privacy policy
            </a>
            .
          </li>
          <li>
            <strong>Cloud database hosting — MongoDB Atlas (MongoDB, Inc.)</strong> in EU-based
            data centres.
          </li>
          <li>
            <strong>Application hosting — Emergent Labs</strong> for preview and deployment
            infrastructure.
          </li>
          <li>
            <strong>Authorities</strong>: where required by law, court order, or to prevent fraud.
          </li>
        </ul>
        <p style={pStyle}>
          We <strong>do not</strong> sell or rent your personal data to advertisers or data brokers.
        </p>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>5. International data transfers</h2>
        <p style={pStyle}>
          Some processors (Resend, Twilio) are located in the United States. Transfers are
          protected by the EU Standard Contractual Clauses (2021/914) and, where available, the
          EU–US Data Privacy Framework.
        </p>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>6. How long we keep your data</h2>
        <ul style={listStyle}>
          <li><strong>Account data</strong> — while your account is active, plus 6 months after deletion request.</li>
          <li><strong>Financial and tax records</strong> — 10 years (statutory retention in the EU).</li>
          <li><strong>Messages inside in-app chat</strong> — up to 24 months.</li>
          <li><strong>Crash logs and session logs</strong> — 90 days.</li>
          <li><strong>Marketing opt-in records</strong> — until you unsubscribe + 24 months.</li>
        </ul>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>7. Your rights</h2>
        <p style={pStyle}>
          Under GDPR and similar laws you have the right to:
        </p>
        <ul style={listStyle}>
          <li>Access the personal data we hold about you.</li>
          <li>Ask us to correct inaccurate data.</li>
          <li>
            Ask us to delete your data (“right to be forgotten”) — subject to legal retention
            obligations.
          </li>
          <li>Ask us to restrict or object to processing.</li>
          <li>Receive your data in a portable, machine-readable format (data portability).</li>
          <li>Withdraw consent you previously gave, at any time.</li>
          <li>
            Lodge a complaint with your national supervisory authority (in the UK — the
            Information Commissioner’s Office; in Greece — the Hellenic DPA).
          </li>
        </ul>
        <p style={pStyle}>
          To exercise any of these rights, email{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: '#703FF6' }}>
            {SUPPORT_EMAIL}
          </a>
          . We respond within 30 days.
        </p>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>8. Security</h2>
        <p style={pStyle}>
          We use industry-standard security measures: TLS 1.2+ in transit, encryption at rest,
          bcrypt-hashed passwords, JWT access tokens, role-based access controls, and audit logs.
          Despite all reasonable precautions, no system is 100% secure; if we detect a breach
          likely to result in a risk to your rights, we will notify you and the relevant
          authority within 72 hours as required by GDPR Art. 33–34.
        </p>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>9. Cookies and local storage</h2>
        <p style={pStyle}>
          The Allure app stores a session token, your preferred role, and some UX preferences in
          your device’s local storage. These are <strong>strictly necessary</strong> for the service to function
          and therefore do not require consent under the ePrivacy Directive.
        </p>
        <p style={pStyle}>
          With your explicit opt-in via the cookie consent banner, we also use an{' '}
          <strong>analytics &amp; attribution</strong> service (Airbridge, operated by AB180 Inc.) to measure how
          invite links are shared and how new users discover the app. Airbridge stores a small
          identifier in your browser/device storage to associate a tap on an invite link with the
          subsequent sign-up. This processing is based on your consent (Art. 6(1)(a) GDPR) and you
          can withdraw it at any time from <em>Settings → Privacy → Cookie preferences</em>. Without
          your consent, no analytics identifiers are stored and no events are sent to Airbridge.
        </p>
        <p style={pStyle}>
          We do not use third-party advertising or re-targeting cookies. Airbridge is used solely
          for measurement of organic invite growth.
        </p>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>10. Children</h2>
        <p style={pStyle}>
          Allure is not directed at children under 16. We do not knowingly collect personal data
          from minors. If you believe a child has provided us with data, contact us and we will
          delete it.
        </p>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>11. Changes to this policy</h2>
        <p style={pStyle}>
          We may update this policy from time to time. Material changes will be announced in the
          app and by email at least 14 days before they take effect. The “Last updated” date at
          the top of this page always reflects the current version.
        </p>
      </section>

      <section style={sectionStyle}>
        <h2 style={h2Style}>12. Contact</h2>
        <p style={pStyle}>
          <strong>{COMPANY.nameLatin}</strong><br />
          {COMPANY.addressLatin}<br />
          Γ.Ε.ΜΗ. {COMPANY.gemi} — ΑΦΜ {COMPANY.vat} — ΔΟΥ {COMPANY.taxOffice}<br />
          Phone: {COMPANY.phone}<br />
          Email:{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: '#703FF6' }}>
            {SUPPORT_EMAIL}
          </a>
          <br />
          See also our{' '}
          <Link to="/terms" style={{ color: '#703FF6' }} data-testid="privacy-terms-link">
            Terms of Service
          </Link>
          {' '}and our{' '}
          <Link to="/legal/imprint" style={{ color: '#703FF6' }} data-testid="privacy-imprint-link">
            Legal / Imprint
          </Link>
          {' '}page.
        </p>
      </section>
    </div>
  );
}
